import React, { useEffect, useState } from 'react';

/**
 * DEV-ONLY build/version footer. Fixed bottom-center, muted/low-contrast, and NON-INTERACTIVE
 * (pointer-events:none). Fetches the public GET /api/version once on mount and renders
 * `biombo v<version>_<commit>`.
 *
 * 🔴 ABSENT FROM EVERY BUILT ARTIFACT. It used to ship unconditionally, and at 390px the pill landed
 * dead-centre on the message composer and over the grid's bottom-row window chip. `pointer-events-none`
 * meant it blocked no CLICK, which is exactly why it survived — every interaction test passed straight
 * through it while the operator saw a debug overlay glued to the input. Occlusion is the damage, not
 * interception.
 *
 * The gate is `import.meta.env.DEV`, which is false in ANY `vite build` output — stg, prod, and the
 * artifact the e2e suite runs against. That is deliberate default-deny: there is no env var to set, so
 * there is nothing to forget to set, and no way for a missing value to fail OPEN and put it back in front
 * of the operator. The capability itself is untouched — GET /api/version still answers, which is where a
 * deployed sha should be read from.
 */
const VersionFooter: React.FC = () => {
  const [label, setLabel] = useState<string>('');

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    let cancelled = false;
    fetch('/api/version')
      .then((r) => r.json())
      .then((d) => { if (!cancelled && d?.version) setLabel(`biombo v${d.version}_${d.commit ?? 'dev'}`); })
      .catch(() => { /* on failure, render nothing */ });
    return () => { cancelled = true; };
  }, []);

  if (!import.meta.env.DEV) return null;
  if (!label) return null;

  return (
    <div
      aria-hidden="true"
      // Fixed bottom-center. On mobile the console's bottom-bar (fixed, ~64px, z-50) occupies the
      // bottom edge, so lift the footer clear of it (bottom-[72px]); on md+ there is no bottom-bar,
      // so it sits at the very bottom. pointer-events-none = never blocks the UI. (Unified overhaul.)
      className="fixed left-0 right-0 text-center pointer-events-none select-none z-[60] bottom-[72px] md:bottom-1.5"
    >
      {/* readable-but-unobtrusive pill: clearly visible to the eye, still subtle */}
      <span
        style={{
          display: 'inline-block',
          padding: '3px 10px',
          borderRadius: 9999,
          fontSize: '11px',
          lineHeight: 1,
          letterSpacing: '0.03em',
          color: 'rgba(255,255,255,0.72)',
          background: 'rgba(0,0,0,0.42)',
          border: '1px solid rgba(255,255,255,0.14)',
          backdropFilter: 'blur(2px)',
        }}
      >
        {label}
      </span>
    </div>
  );
};

export default VersionFooter;
